import { useCallback, useEffect, useMemo, useState } from 'react';

import {
  Stack,
  Tab,
  TabList,
  Tabs,
  TabPanels,
  TabPanel,
  Icon,
  IconButton,
  Text,
  Tooltip,
  useColorModeValue,
  useDisclosure,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  ButtonGroup,
} from '@ecoinvent/ui';
import mixpanel from 'mixpanel-browser';
import { BiSearch, BiX } from 'react-icons/bi';
import { IoGitBranchOutline } from 'react-icons/io5';
import { MdLogin, MdLogout, MdOutlineEco, MdOutlineTableRows, MdVilla } from 'react-icons/md';
import { localStorageKeys } from 'storageKeys';

import { ecosphereFlows, FlowsInput, FlowsOutput, FlowsType, technosphereFlows } from 'hooks/data/datasets/V4/flows/types';
import useFlowDetails from 'hooks/data/datasets/V4/flows/useFlowDetails';
import { MIXPANEL_EVENTS } from 'utilities/constants/mixpanel';

import FlowDetailDrawer from './FlowDetailDrawer';
import FlowGraphView from './FlowGraphView';
import FlowsViewCard from './FlowViewCard';
import NoFlowView from './NoFlowView';
import { DIRECTION_KEYWORDS, GROUP_KEYWORDS } from './utils/constants';
import ViewOptionsMenu from './ViewOptionsMenu';

type Props = {
  technosphereFlows: technosphereFlows;
  ecosphereFlows: ecosphereFlows;
  datasetId: string;
};

const loadFlowLayout = () => {
  const flowLayout = localStorage.getItem(localStorageKeys.FLOW_LAYOUT);
  return (flowLayout ? JSON.parse(flowLayout) : true) as boolean;
};

const saveFlowLayout = (isHorizontalLayout: boolean) => {
  localStorage.setItem(localStorageKeys.FLOW_LAYOUT, JSON.stringify(isHorizontalLayout));
};

type ViewMode = 'list' | 'graph';

const loadViewMode = (): ViewMode => {
  const stored = localStorage.getItem(localStorageKeys.FLOW_VIEW_MODE);
  return stored === 'graph' ? 'graph' : 'list';
};

const saveViewMode = (mode: ViewMode) => {
  localStorage.setItem(localStorageKeys.FLOW_VIEW_MODE, mode);
};

const FlowsView = ({ technosphereFlows, ecosphereFlows, datasetId }: Props) => {
  const [viewMode, setViewMode] = useState<ViewMode>(loadViewMode);
  const [isGraphFullScreen, setIsGraphFullScreen] = useState(false);
  const [flowId, setFlowId] = useState<string | undefined>();
  const [flowType, setFlowType] = useState<FlowsType>('inputs');
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [filterValue, setFilterValue] = useState('');
  const [isHorizontalLayout, setIsHorizontalLayout] = useState(() => {
    return loadFlowLayout();
  });
  const [showComments, setShowComments] = useState(false);
  const [sortBy, setSortBy] = useState('alphabetical-az');
  const [viewOptionDirection, setViewOptionDirection] = useState(['inputs', 'outputs']);
  const [viewOption, setViewOption] = useState(['reference flow', 'valuable', 'waste', 'byproduct', 'resource', 'emission']);

  const { isOpen, onOpen, onClose } = useDisclosure();
  const grayTextColor = useColorModeValue('gray.600', 'gray.400');
  const searchIconColor = useColorModeValue('gray.400', 'gray.500');
  const switcherBg = useColorModeValue('#F2F1F1', 'rgba(255,255,255,0.08)');
  const switcherBorderColor = useColorModeValue('#E7E7E7', 'rgba(255,255,255,0.08)');
  const activeButtonBg = useColorModeValue('white', 'rgba(255,255,255,0.08)');
  const activeIconColor = useColorModeValue('#000000', 'rgba(255,255,255,0.92)');
  const inactiveIconColor = useColorModeValue('#817E7E', '#AEACAC');

  const { data: flowDetails, isLoading: isLoadingFlowDetails } = useFlowDetails(datasetId, flowType, flowId);

  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => {
    saveFlowLayout(isHorizontalLayout);
  }, [isHorizontalLayout]);

  const filterFlows = useCallback(
    (
      flows: (FlowsInput | FlowsOutput)[],
      searchTerm: string,
      direction: 'inputs' | 'outputs'
    ): (FlowsInput | FlowsOutput)[] => {
      let filteredFlows = flows;
      const lowerTerm = searchTerm.toLowerCase().trim();

      const matchedDirectionKey = Object.keys(DIRECTION_KEYWORDS).find((k) => k.startsWith(lowerTerm) && lowerTerm.length > 2);
      const matchedGroupKey = Object.keys(GROUP_KEYWORDS).find((k) => k.startsWith(lowerTerm) && lowerTerm.length > 2);

      if (matchedDirectionKey) {
        filteredFlows = direction === DIRECTION_KEYWORDS[matchedDirectionKey] ? flows : [];
      } else if (matchedGroupKey) {
        filteredFlows = flows.filter((flow) => flow.group === GROUP_KEYWORDS[matchedGroupKey]);
      } else if (searchTerm) {
        filteredFlows = flows.filter((flow) => flow.name.toLowerCase().includes(lowerTerm));
      }

      const viewOptionFilteredFlows = filteredFlows.filter((flow) => {
        return viewOption.includes(flow.group);
      });

      return viewOptionFilteredFlows.sort((a, b) => {
        if (sortBy === 'alphabetical-az') {
          return a.name.localeCompare(b.name);
        } else if (sortBy === 'alphabetical-za') {
          return b.name.localeCompare(a.name);
        } else if (sortBy === 'highest-amount') {
          return b.amount - a.amount;
        } else if (sortBy === 'lowest-amount') {
          return a.amount - b.amount;
        }
        return 0;
      });
    },
    [sortBy, viewOption]
  );

  const filteredTechnosphereInputs = useMemo(
    () => filterFlows(technosphereFlows.inputs, filterValue, 'inputs'),
    [filterFlows, technosphereFlows.inputs, filterValue]
  );

  const filteredTechnosphereOutputs = useMemo(
    () => filterFlows(technosphereFlows.outputs, filterValue, 'outputs'),
    [filterFlows, technosphereFlows.outputs, filterValue]
  );

  const filteredEcosphereInputs = useMemo(
    () => filterFlows(ecosphereFlows.inputs, filterValue, 'inputs'),
    [filterFlows, ecosphereFlows.inputs, filterValue]
  );

  const filteredEcosphereOutputs = useMemo(
    () => filterFlows(ecosphereFlows.outputs, filterValue, 'outputs'),
    [filterFlows, ecosphereFlows.outputs, filterValue]
  );

  const technosphereFlowsCount = filteredTechnosphereInputs.length + filteredTechnosphereOutputs.length;
  const ecosphereFlowsCount = filteredEcosphereInputs.length + filteredEcosphereOutputs.length;

  const handleClick = useCallback(
    (flowId: string, flowType: FlowsType) => {
      setFlowId(flowId);
      setFlowType(flowType);
      onOpen();
    },
    [onOpen]
  );

  const handleFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFilterValue(e.target.value);
  };

  const handleViewModeChange = (mode: ViewMode) => {
    if (mode === 'graph') setFilterValue('');
    setViewMode(mode);
    saveViewMode(mode);
  };

  const onChangeTab = (newValue: number) => {
    setActiveTab(newValue);
  };

  const flowListBg = useColorModeValue('gray.50', 'gray.800');

  const pluralize = (items: number) => {
    return items > 1 ? 's' : '';
  };

  const clearFilter = () => {
    setFilterValue('');
    mixpanel.track(MIXPANEL_EVENTS.FLOW_SEARCH_CLEARED);
  };

  return (
    <Stack direction={'row'} spacing={6}>
      <Stack w={'100%'} spacing={2}>
        <Tabs isLazy index={activeTab} onChange={onChangeTab} w={'100%'} variant={'soft-rounded'}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Stack direction="row">
              <TabList>
                <Tab fontWeight={'semi-bold'} borderRadius={'lg'}>
                  <Icon as={MdVilla} mr={1} w="20px" height="20px" />
                  Technosphere ({technosphereFlowsCount})
                </Tab>
                <Tab fontWeight={'semi-bold'} borderRadius={'lg'}>
                  <Icon as={MdOutlineEco} mr={1} w="20px" height="20px" />
                  Ecosphere ({ecosphereFlowsCount})
                </Tab>
              </TabList>
            </Stack>
            <Stack direction="row">
              {viewMode === 'list' && (
                <InputGroup w="100%" h="42px" _focusWithin={{ w: { base: 'auto', xl: '30vw' } }}>
                  <InputLeftElement pointerEvents="none" h="42px">
                    <Icon as={BiSearch} color={searchIconColor} />
                  </InputLeftElement>
                  <Input
                    h="42px"
                    borderRadius="md"
                    type="search"
                    placeholder="Filter flows..."
                    value={filterValue}
                    onChange={handleFilterChange}
                    transition="all 0.3s ease-in-out"
                    sx={{
                      '&::-webkit-search-cancel-button': { display: 'none' },
                      '&::-webkit-search-decoration': { display: 'none' },
                    }}
                    onFocus={() => mixpanel.track(MIXPANEL_EVENTS.FLOW_SEARCH_BAR_CLICKED)}
                  />
                  {filterValue && (
                    <InputRightElement cursor="pointer" h="42px" onClick={clearFilter}>
                      <Icon as={BiX} width="20px" height="20px" color={searchIconColor} />
                    </InputRightElement>
                  )}
                </InputGroup>
              )}
              {viewMode === 'list' && (
                <ViewOptionsMenu
                  showComments={showComments}
                  setShowComments={setShowComments}
                  isHorizontalLayout={isHorizontalLayout}
                  setIsHorizontalLayout={setIsHorizontalLayout}
                  viewOptionDirection={viewOptionDirection}
                  setViewOptionDirection={setViewOptionDirection}
                  viewOption={viewOption}
                  setViewOption={setViewOption}
                  sortBy={sortBy}
                  setSortBy={setSortBy}
                />
              )}
              <ButtonGroup
                size="md"
                bg={switcherBg}
                borderRadius="8px"
                border="1px solid"
                borderColor={switcherBorderColor}
                p="2px"
                spacing={0.5}
              >
                <Tooltip label="Table" hasArrow placement="top">
                  <IconButton
                    aria-label="Table view"
                    icon={<Icon as={MdOutlineTableRows} color={viewMode === 'list' ? activeIconColor : inactiveIconColor} />}
                    onClick={() => handleViewModeChange('list')}
                    variant="ghost"
                    colorScheme="gray"
                    bg={viewMode === 'list' ? activeButtonBg : 'transparent'}
                    borderRadius="md"
                    h="9"
                    w="9"
                    minW="9"
                    _hover={{ bg: activeButtonBg }}
                    sx={{ '&:hover svg': { color: activeIconColor } }}
                  />
                </Tooltip>
                <Tooltip label="Graph" hasArrow placement="top">
                  <IconButton
                    aria-label="Graph view"
                    icon={
                      <Icon
                        as={IoGitBranchOutline}
                        transform="rotate(90deg)"
                        color={viewMode === 'graph' ? activeIconColor : inactiveIconColor}
                      />
                    }
                    onClick={() => handleViewModeChange('graph')}
                    variant="ghost"
                    colorScheme="gray"
                    bg={viewMode === 'graph' ? activeButtonBg : 'transparent'}
                    borderRadius="md"
                    h="9"
                    w="9"
                    minW="9"
                    _hover={{ bg: activeButtonBg }}
                    sx={{ '&:hover svg': { color: activeIconColor } }}
                  />
                </Tooltip>
              </ButtonGroup>
            </Stack>
          </Stack>

          <TabPanels mt={4}>
            <TabPanel p={0}>
              {viewMode === 'graph' ? (
                <FlowGraphView
                  datasetId={datasetId}
                  inputs={filteredTechnosphereInputs}
                  outputs={filteredTechnosphereOutputs}
                  technosphereInputs={filteredTechnosphereInputs}
                  technosphereOutputs={filteredTechnosphereOutputs}
                  ecosphereInputs={filteredEcosphereInputs}
                  ecosphereOutputs={filteredEcosphereOutputs}
                  flowTabType="technosphere"
                  isFullScreen={isGraphFullScreen}
                  setIsFullScreen={setIsGraphFullScreen}
                  onFlowClick={handleClick}
                  filterValue={filterValue}
                  setFilterValue={setFilterValue}
                  onFilterClear={clearFilter}
                />
              ) : (
                <Stack direction={isHorizontalLayout ? 'row' : 'column'} minHeight="500px" alignItems="stretch" spacing={6}>
                  {viewOptionDirection.includes('inputs') && (
                    <Stack
                      bgColor={flowListBg}
                      display="flex"
                      flexDirection="column"
                      w={isHorizontalLayout ? '50%' : '100%'}
                      p={3}
                      pt={6}
                      spacing={6}
                      borderRadius={'lg'}
                    >
                      <Stack alignItems="center" flexDirection="row" spacing={2} fontWeight="bold" color={grayTextColor}>
                        <Icon as={MdLogin} width="20px" height="20px" />
                        <Text>
                          Input{`${pluralize(filteredTechnosphereInputs.length)}`} {`(${filteredTechnosphereInputs.length})`}
                        </Text>
                      </Stack>

                      {filteredTechnosphereInputs.length > 0 ? (
                        <Stack spacing={2}>
                          {filteredTechnosphereInputs.map((flow) => (
                            <FlowsViewCard
                              key={flow.id}
                              flow={flow}
                              flowType="inputs"
                              handleClick={handleClick}
                              showComments={showComments}
                            />
                          ))}
                        </Stack>
                      ) : (
                        <NoFlowView />
                      )}
                    </Stack>
                  )}

                  {viewOptionDirection.includes('outputs') && (
                    <Stack
                      bgColor={flowListBg}
                      display="flex"
                      flexDirection="column"
                      w={isHorizontalLayout ? '50%' : '100%'}
                      p={3}
                      pt={6}
                      spacing={6}
                      borderRadius={'lg'}
                    >
                      <Stack alignItems="center" flexDirection="row" spacing={2} fontWeight="bold" color={grayTextColor}>
                        <Icon as={MdLogout} width="20px" height="20px" />
                        <Text>
                          Output{`${pluralize(filteredTechnosphereOutputs.length)}`} {`(${filteredTechnosphereOutputs.length})`}
                        </Text>
                      </Stack>
                      {filteredTechnosphereOutputs.length > 0 ? (
                        <Stack spacing={2}>
                          {filteredTechnosphereOutputs.map((flow) => (
                            <FlowsViewCard
                              key={flow.id}
                              flow={flow}
                              flowType="outputs"
                              handleClick={handleClick}
                              showComments={showComments}
                            />
                          ))}
                        </Stack>
                      ) : (
                        <NoFlowView />
                      )}
                    </Stack>
                  )}
                </Stack>
              )}
            </TabPanel>
            <TabPanel p={0}>
              {viewMode === 'graph' ? (
                <FlowGraphView
                  datasetId={datasetId}
                  inputs={filteredEcosphereInputs}
                  outputs={filteredEcosphereOutputs}
                  technosphereInputs={filteredTechnosphereInputs}
                  technosphereOutputs={filteredTechnosphereOutputs}
                  ecosphereInputs={filteredEcosphereInputs}
                  ecosphereOutputs={filteredEcosphereOutputs}
                  flowTabType="ecosphere"
                  isFullScreen={isGraphFullScreen}
                  setIsFullScreen={setIsGraphFullScreen}
                  onFlowClick={handleClick}
                  filterValue={filterValue}
                  setFilterValue={setFilterValue}
                  onFilterClear={clearFilter}
                />
              ) : (
                <Stack direction={isHorizontalLayout ? 'row' : 'column'} minHeight="500px" alignItems="stretch" spacing={5}>
                  {viewOptionDirection.includes('inputs') && (
                    <Stack
                      bgColor={flowListBg}
                      display="flex"
                      flexDirection="column"
                      w={isHorizontalLayout ? '50%' : '100%'}
                      p={3}
                      pt={6}
                      spacing={6}
                      borderRadius={'lg'}
                    >
                      <Stack alignItems="center" flexDirection="row" spacing={2} pb={4} fontWeight="bold" color={grayTextColor}>
                        <Icon as={MdLogin} width="20px" height="20px" />
                        <Text>
                          Extraction{`${pluralize(filteredEcosphereInputs.length)}`} {`(${filteredEcosphereInputs.length})`}
                        </Text>
                      </Stack>
                      {filteredEcosphereInputs.length > 0 ? (
                        <Stack spacing={2}>
                          {filteredEcosphereInputs.map((flow) => (
                            <FlowsViewCard
                              key={flow.id}
                              flow={flow}
                              flowType="ecosphere"
                              handleClick={handleClick}
                              showComments={showComments}
                            />
                          ))}
                        </Stack>
                      ) : (
                        <NoFlowView />
                      )}
                    </Stack>
                  )}

                  {viewOptionDirection.includes('outputs') && (
                    <Stack
                      bgColor={flowListBg}
                      display="flex"
                      flexDirection="column"
                      w={isHorizontalLayout ? '50%' : '100%'}
                      p={3}
                      pt={6}
                      spacing={6}
                      borderRadius={'lg'}
                    >
                      <Stack alignItems="center" flexDirection="row" spacing={2} pb={4} fontWeight="bold" color={grayTextColor}>
                        <Icon as={MdLogout} width="20px" height="20px" />
                        <Text>
                          Emission{`${pluralize(filteredEcosphereOutputs.length)}`} {`(${filteredEcosphereOutputs.length})`}
                        </Text>
                      </Stack>

                      {filteredEcosphereOutputs.length > 0 ? (
                        <Stack spacing={2}>
                          {filteredEcosphereOutputs.map((flow) => (
                            <FlowsViewCard
                              key={flow.id}
                              flow={flow}
                              flowType="ecosphere"
                              handleClick={handleClick}
                              showComments={showComments}
                            />
                          ))}
                        </Stack>
                      ) : (
                        <NoFlowView />
                      )}
                    </Stack>
                  )}
                </Stack>
              )}
            </TabPanel>
          </TabPanels>
        </Tabs>
      </Stack>
      <FlowDetailDrawer
        isOpen={isOpen}
        onClose={onClose}
        isFullScreen={isFullScreen}
        setIsFullScreen={setIsFullScreen}
        flowDetails={flowDetails}
        isLoading={isLoadingFlowDetails}
      />
    </Stack>
  );
};

export default FlowsView;
